import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { EDITION } from "@/lib/edition";
import { Trophy, HandHeart, GraduationCap, Student, Buildings } from "@phosphor-icons/react";

const CAT_META = {
  top_champions: { title: "Top Champions", icon: "🌟" },
  top_supporters: { title: "Top Supporters", icon: "🏅" },
  mentors: { title: "Mentors", icon: "👨‍🏫" },
  scholarship_sponsors: { title: "Scholarship Sponsors", icon: "🎓" },
  technology_donors: { title: "Technology Donors", icon: "💻" },
  research_supporters: { title: "Research Supporters", icon: "🔬" },
  career_builders: { title: "Career Builders", icon: "💼" },
  community_champions: { title: "Community Champions", icon: "🌎" },
};

function PeopleCard({ catKey, people }) {
  const m = CAT_META[catKey];
  if (!people?.length) return null;
  return (
    <div className="rounded-2xl border border-border/60 bg-white p-5" data-testid={`impact-cat-${catKey}`}>
      <div className="flex items-center gap-2"><span className="text-xl">{m.icon}</span><h3 className="font-heading text-lg font-medium">{m.title}</h3></div>
      <div className="mt-3 space-y-2">
        {people.map((p) => (
          <Link key={p.user_id} to={`/u/${p.user_id}`} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/50">
            <span className="truncate text-sm font-medium">{p.name || "Alumni supporter"}</span>
            <span className="shrink-0 rounded-full bg-secondary/10 px-2 py-0.5 text-[11px] text-secondary">{p.level}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function AlumniPulseImpact() {
  const [d, setD] = useState(null);
  useSeo("AlumniPulse Impact", "Celebrating the alumni and institutions building the future together — supporters, mentors, scholarship sponsors and completed projects.");
  useEffect(() => { api.get("/impact/leaderboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  return (
    <div className="mx-auto max-w-6xl px-5 py-14" data-testid="alumnipulse-impact-page">
      <div className="text-center">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary/15 text-secondary"><Trophy size={30} weight="duotone" /></div>
        <h1 className="mt-4 font-heading text-4xl font-light tracking-tight sm:text-5xl">{EDITION.brand} Impact</h1>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Celebrating alumni and institutions building the future together. This isn't about who gives the most money —
          it's about who gives back. {EDITION.brand} only connects people; every contribution is arranged directly with the institution.
        </p>
      </div>

      {d && d !== false && (
        <>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: HandHeart, label: "Alumni supporters", value: d.stats.total_supporters },
              { icon: GraduationCap, label: "Verified contributions", value: d.stats.verified_contributions },
              { icon: Buildings, label: "Completed projects", value: d.stats.completed_projects },
              { icon: Student, label: "Active needs", value: d.stats.active_needs },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-border/60 bg-white p-5 text-center">
                <s.icon className="mx-auto text-secondary" size={22} weight="duotone" />
                <div className="mt-2 font-heading text-2xl font-medium">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Object.keys(CAT_META).map((k) => <PeopleCard key={k} catKey={k} people={d.categories[k]} />)}
          </div>

          {(d.top_projects?.length > 0 || d.recently_completed?.length > 0) && (
            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              {d.top_projects?.length > 0 && (
                <div>
                  <h2 className="font-heading text-2xl font-medium">Most-supported projects</h2>
                  <div className="mt-3 space-y-2">
                    {d.top_projects.map((p) => (
                      <Link key={p.id} to={`/projects/${p.id}`} className="block rounded-xl border border-border/60 bg-white p-4 hover:border-secondary/50">
                        <div className="flex items-center justify-between gap-2"><span className="font-medium">{p.title}</span><span className="text-xs text-secondary">{p.supporter_count} supporters</span></div>
                        <div className="text-xs text-muted-foreground">{p.institution_name} · {p.category}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {d.recently_completed?.length > 0 && (
                <div>
                  <h2 className="font-heading text-2xl font-medium">Recently completed 🎉</h2>
                  <div className="mt-3 space-y-2">
                    {d.recently_completed.map((p) => (
                      <Link key={p.id} to={`/projects/${p.id}`} className="block rounded-xl border border-secondary/30 bg-secondary/5 p-4 hover:border-secondary/60">
                        <div className="font-medium">{p.title}</div>
                        <div className="text-xs text-muted-foreground">{p.institution_name} · {p.students_affected ? `${p.students_affected} students helped` : p.category}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {d.stats.total_supporters === 0 && (
            <p className="mt-10 text-center text-muted-foreground">No verified contributions yet — be the first to help your alma mater and get recognized here.</p>
          )}
        </>
      )}
    </div>
  );
}
